// src/config/modulePages.js
// 占位菜单 → 统一骨架页配置。
// 每个条目依据《云平台（能源管家）需求文档》等 4 份需求文档的字段/模块划分，
// 声明该页的筛选栏、操作按钮与内容区块；由 ModulePage + BlockRenderer 统一渲染。
// 数据统一取 stationData.js 演示数据（后端未接入）。
import dayjs from 'dayjs';
import {
  STATIONS, PERM_TREE,
  MONTH_TREND,
  EMS_PERIODS, TARIFF_ROWS,
  // B 批次：电池分析（doc02 2.13）
  getStackVoltageWarn, getStackVoltageWarnSeries,
  getClusterVoltageSeries, getCellVoltageDiffTrend, getCellVoltageDiffDistribution,
  getCellVoltageDistribution, getCellVoltageTable, getCellVoltageCurve,
  getStackCurrentWarnSeries,
  getClusterTempAvgMaxMin, getClusterTempDiffSeries, getCellTempRise,
  getCellTempDistribution, getCellTempTable, getCellTempCurve,
  getLaggingReport,
  // C 批次：核算管理（doc01 2.20）
  SETTLE_RECORDS, SETTLE_RULES, TARIFF_TEMPLATES, SETTLE_MONTH_TREND, SETTLE_STAT, SETTLE_BY_STATION,
  // C 批次：站点管理（doc01 3.1）
  STATION_TAGS, OWN_STATIONS, AUTHORIZED_STATIONS,
  // D 批次：数据大屏 / 站点地图 / 三维（doc01 2.1 / 2.4 / 2.5）
  SCREEN_DAILY, getSocialBenefit, getIncomeRanking,
  getMapMetrics, MAP_POINTS,
  // 多站概览（doc01 2.2）
  getMultiMetrics, getStationIncome, MULTI_ENERGY,
  getMultiEfficiencyTrend, getEfficiencyRanking,
  getAlarmTrend, getAlarmByType, getAlarmByLevel, getAlarmRanking,
  // 单站概览（doc01 2.3）
  getSingleCard, getSingleIncome, getSingleEnergy, getSingleEfficiency, getSingleAlarm,
  // E 批次：监控中心（doc01 2.6 / 2.8 / 2.9）
  getRealtimeList, getRealtimeDetail, CENTRAL_TARIFF, STATION_REGIONS,
  // G 批次：运维中心 · 计划 / 运维图谱 / 运维账号（doc01 2.16 ~ 2.18）
  // （2.16 计划 / 2.17 运维图谱已改为独立页面组件，所需枚举 / 数据直接取自 stationData）
  OM_ACCOUNT_ROWS,
  // G 批次：运营中心 · 运营分析（doc01 2.21）
  OP_MULTI_REPORTS, OP_SINGLE_REPORTS, getOpMultiSummary, OP_STATION_DETAIL, OP_GRADE_PIE,
  getOpSingleSummary, getOpSingleMonth, getOpSingleTrend,
  // H 批次：管理中心 · 组织成员 / 角色 / 外部公司 / 日志 / 个性配置（doc01 3.2 ~ 3.6）
  H_DEPT_TREE, H_MEMBERS,
  H_ROLES,
  H_COMPANIES, H_COMPANY_PERMS,
  H_LOGS,
  H_THEME_ITEMS,
} from './stationData';

// ---------------- 通用筛选 / 操作 ----------------
const F_STATION = { key: 'station', type: 'select', label: '站点', width: 200, options: STATIONS.map((s) => ({ value: s.id, label: s.name })) };
const F_RANGE = { key: 'range', type: 'daterange', label: '时间' };

const A_REFRESH = { key: 'refresh', label: '刷新' };
const A_EXPORT = { key: 'export', label: '导出' };
const A_NEW = (label) => ({ key: 'new', label, primary: true });

// 监控中心查询区：省 / 市 / 区三级 + 站点名称模糊匹配（doc01 2.6 / 2.8 / 2.9）
// 78 地区选项由站点行政区划（STATION_REGIONS）自动解析，避免写死导致新站点（如秀洲区）筛不到
const regionOptions = (pick) => Array.from(new Set(Object.values(STATION_REGIONS).map(pick)))
  .filter(Boolean).map((v) => ({ value: v, label: v }));
const F_REGION = [
  { key: 'province', type: 'select', label: '省', width: 130, options: regionOptions((r) => r.province) },
  { key: 'city', type: 'select', label: '市', width: 130, options: regionOptions((r) => r.city) },
  { key: 'district', type: 'select', label: '区', width: 140, options: regionOptions((r) => r.district) },
];
const F_STATION_NAME = { key: 'kw', type: 'search', label: '站点名称', placeholder: '输入站点名称模糊匹配' };
const A_QUERY = { key: 'query', label: '查询', primary: true };
const A_RESET = { key: 'reset', label: '重置' };

// ---------------- 状态色板 ----------------
const T = {
  level: { 紧急: 'red', 高: 'orange', 中: 'blue', 低: 'default' },
  woState: { 待接单: 'orange', 待执行: 'blue', 已完成: 'green', 已关闭: 'default' },
  woSource: { 报警转工单: 'red', APP上报: 'blue', 计划生成: 'cyan', 手动创建: 'default' },
  result: { 成功: 'green', 失败: 'red', '-': 'default' },
  memberState: { 启用: 'green', 停用: 'default' },
  runState: { 执行中: 'blue', 待启动: 'default', 已完成: 'green' },
  eventState: { 已结算: 'green', 响应中: 'blue', 待申报: 'orange' },
  tariffKind: { 谷: 'green', 平: 'blue', 峰: 'orange', 尖: 'red' },
  grade: { 优: 'green', 良: 'blue', 中: 'orange', 差: 'red' },
  assetState: { 在役: 'green', 退役: 'default' },
  companyState: { 合作中: 'green', 洽谈中: 'orange' },
  commState: { 正常: 'green', 延迟: 'orange', 离线: 'red' },
  enabled: { 启用: 'green', 停用: 'default' },
  settleState: { 已出账: 'green', 待出账: 'orange' },
  inviteState: { 生效中: 'green', 已用尽: 'default', 已失效: 'red' },
  confirmState: { 已确认: 'green', 未确认: 'orange' },
  ruleState: { 生效: 'green', 失效: 'default' },
  visibleState: { 显示: 'green', 隐藏: 'default' },
  predictCfg: { 已配置: 'green', 未配置: 'default' },
  planKind: { 巡视检查: 'blue', 维护保养: 'cyan' },
  opGrade: { 优秀: 'green', 良好: 'blue', 待提升: 'orange' },
  reportState: { 已生成: 'green', 生成中: 'blue', 失败: 'red' },
};

const MONEY = (title, dataIndex) => ({ title, dataIndex, num: true, width: 120 });

// 电池分析预警阈值（供 KPI 展示，取自演示数据契约）
const V_WARN = getStackVoltageWarn();

// 电池分析：卡片内筛选控件（缺陷 92：筛选放在图 / 表格卡片内、内容上方，不再放页面顶部）
const BAT_STACKS = [{ value: 'S1', label: '1#电堆' }, { value: 'S2', label: '2#电堆' }];
const BAT_RACKS = [1, 2, 3, 4, 5].map((n) => ({ value: `R${n}`, label: `${n}#电池簇` }));
const BAT_CELLS = [38, 61, 12, 104, 1].map((n) => ({ value: `C${n}`, label: `${n}#单体` }));
const BAT_CONSISTENCY = ['优', '良', '中', '差'].map((v) => ({ value: v, label: v }));
const BAT_YDAY = [dayjs().subtract(1, 'day'), dayjs().subtract(1, 'day')];
const ctlStack = { key: 'stack', type: 'select', label: '电堆', options: BAT_STACKS, defaultValue: 'S1', width: 110 };
const ctlRack = { key: 'rack', type: 'select', label: '电池簇', options: BAT_RACKS, defaultValue: 'R1', width: 120 };
const ctlTime = { key: 'time', type: 'daterange', label: '时间', defaultValue: BAT_YDAY, width: 230 };
const ctlCell = { key: 'cell', type: 'select', label: '单体', options: BAT_CELLS, defaultValue: 'C38', width: 120 };
const ctlConsistency = { key: 'consistency', type: 'select', label: '一致性', options: BAT_CONSISTENCY, defaultValue: '优', width: 90 };
const btnQuery = { key: 'query', type: 'button', label: '查询', primary: true, tip: '已按当前条件查询' };
const btnReset = { key: 'reset', type: 'button', label: '重置', tip: '筛选条件已重置' };

// D 批次：大屏 / 地图 / 三维概览（doc01 2.1 / 2.4 / 2.5）
const BENEFIT = getSocialBenefit();
const MAP_METRICS = getMapMetrics();
const INCOME_RANKING = getIncomeRanking();

// 多站概览（doc01 2.2）
const MULTI_M = getMultiMetrics();
const STATION_INCOME = getStationIncome();
const EFF_TREND = getMultiEfficiencyTrend();
const EFF_RANK = getEfficiencyRanking();
const ALARM_TREND = getAlarmTrend();
const ALARM_TYPE = getAlarmByType();
const ALARM_LEVEL = getAlarmByLevel();
const ALARM_RANK = getAlarmRanking();

// 单站概览（doc01 2.3）：演示站点取海宁工业园站
const SINGLE_CARD = getSingleCard('hn');
const SINGLE_INCOME = getSingleIncome('hn');
const SINGLE_ENERGY = getSingleEnergy('hn');
const SINGLE_EFF = getSingleEfficiency('hn');
const SINGLE_ALARM = getSingleAlarm('hn');

// 监控中心（doc01 2.6）：实时运行详情演示站点取海宁工业园站
const REALTIME_DETAIL = getRealtimeDetail('hn');

// 工单（doc01 2.15）已改为独立页面组件 src/pages/om/WorkOrderPage.js（真实 CRUD / 详情弹层），此处不再配置。

// ---- G 批次：运营分析（doc01 2.21）多站报告 / 单站报告数据 ----
const OP_M = getOpMultiSummary();
const OP_SINGLE_TABS = ['hn', 'nx', 'yx', 'xz'].map((id) => getOpSingleSummary(id));
const OP_MONTH = getOpSingleMonth();
const OP_TREND = getOpSingleTrend();

// 单站运营报告区块（总计区域 / 运营概况 / 本月明细 / 收益趋势），多站报告各站详情复用
const opSingleBlocks = (s) => [
  {
    type: 'info', title: '总计区域',
    items: [
      ['报告标题', `${s.site} · ${s.month}`],
      ['装机功率 / 装机容量', `${s.power} / ${s.capacity}`],
      ['投运时间', s.onlineAt],
      ['本月运行天数', `${s.days} 天（本月内状态为「投运中」的天数）`],
      ['导出', '支持导出 word 报告'],
    ],
  },
  {
    type: 'stat', title: '运营概况',
    items: [
      { label: '本月收益', value: s.income.toLocaleString(), unit: '元' },
      { label: '理论收益', value: s.theory.toLocaleString(), unit: '元' },
      { label: '理论收益达成率', value: s.rate, unit: '%' },
      { label: '单位容量日收益', value: s.unitDay, unit: '元/kWh·天' },
      { label: '环比上月', value: `${s.mom > 0 ? '+' : ''}${s.mom}`, unit: '%' },
      { label: '月理论充电量', value: s.chargeTheory.toLocaleString(), unit: 'kWh' },
      { label: '月理论放电量', value: s.dischargeTheory.toLocaleString(), unit: 'kWh' },
      { label: '综合效率 · 理论 / 实际', value: `${s.effTheory}% / ${s.effActual}%` },
    ],
  },
  {
    type: 'row', layout: 'grid-2',
    blocks: [
      {
        type: 'chart', title: '本月明细 · 每日收益与充放电量', chartType: 'composed',
        data: OP_MONTH, xKey: 'label', height: 250,
        series: [
          { key: 'income', name: '收益 (元)', color: '#f4c542' },
          { key: 'charge', name: '充电量 (kWh)', color: '#3a8dde' },
          { key: 'discharge', name: '放电量 (kWh)', color: '#52e8b0' },
        ],
      },
      {
        type: 'chart', title: '收益趋势 · 近 12 个月', chartType: 'composed',
        data: OP_TREND, xKey: 'month', height: 250,
        series: [
          { key: 'unitDay', name: '单位容量日收益 (元/kWh·天)', color: '#00d4ff' },
          { key: 'rate', name: '理论收益达成率 (%)', color: '#52e8b0', dashed: true },
        ],
      },
    ],
  },
];

export const MODULE_PAGES = {
  // ==================== 展示中心 ====================
  // doc01 2.1：站点卡片 / 收益指标 / 电量指标 / 社会效益
  '/app/display/screen': {
    screenConfig: true,
    // 数据大屏为多站聚合总览，站点 / 时间筛选对聚合指标无意义，故不设置筛选栏
    filters: [],
    actions: [A_REFRESH, { key: 'full', label: '全屏展示', primary: true }],
    blocks: (filters, screen, hidden = []) => {
      const hiddenCount = hidden.length;
      const visibleCount = MAP_METRICS.count - hiddenCount;
      return [
      {
        type: 'kpi', title: '站点卡片',
        items: [
          { label: '电站个数', value: String(visibleCount), unit: '个', foot: hiddenCount ? `已隐藏 ${hiddenCount} 个站点，不计入统计` : '隐藏站点不计入统计' },
          { label: '总装机功率', value: '2.55', unit: 'MW' },
          { label: '总装机容量', value: '4.1', unit: 'MWh' },
          { label: '昨日收益', value: '26,840', unit: '元', up: true, foot: '环比前日 +8.6%' },
          { label: '多站累计收益', value: '35.7', unit: '万元', up: true },
        ],
      },
      { type: 'section', title: '地理分布', desc: '站点地理分布总览（官方中国 / 世界标准底图，含审图号）' },
      {
        type: 'map', mode: (screen && screen.map) || 'china', title: '站点地理分布',
        extra: '官方标准底图 · 站点按经纬度落图 · 右侧可隐藏站点（不计入统计）',
        points: MAP_POINTS, hidden,
      },
      { type: 'section', key: 'income', timeFilter: true, title: '收益指标', desc: '多站累计收益与站点收益排名' },
      {
        type: 'row', layout: 'grid-2',
        blocks: [
          {
            type: 'chart', title: '日总收益（近 7 日，不含今天）', extra: '单位：元', chartType: 'bar',
            timeRange: true, data: SCREEN_DAILY, xKey: 'day', height: 250, barSize: 20,
            series: [{ key: 'income', name: '收益', color: '#f4c542' }],
          },
          {
            type: 'chart', title: '收益排名 · Top5', extra: '多站累计收益 · 元', chartType: 'bar',
            data: INCOME_RANKING, xKey: 'name', height: 250, barSize: 18,
            series: [{ key: 'value', name: '累计收益', color: '#00d4ff' }],
          },
        ],
      },
      { type: 'section', key: 'energy', timeFilter: true, title: '电量指标', desc: '日充放电量与累计电量' },
      {
        type: 'row', layout: 'grid-2',
        blocks: [
          {
            type: 'chart', title: '日充放电量（近 7 天，不含今天）', extra: '单位：kWh', chartType: 'bar',
            timeRange: true, data: SCREEN_DAILY, xKey: 'day', height: 250, barSize: 14,
            series: [
              { key: 'charge', name: '充电量', color: '#52e8b0' },
              { key: 'discharge', name: '放电量', color: '#4dd8ff' },
            ],
          },
          {
            type: 'stat', title: '电量指标',
            items: [
              { label: '累计充电量', value: '528', unit: 'MWh' },
              { label: '累计放电量', value: '496', unit: 'MWh' },
            ],
          },
        ],
      },
      { type: 'section', key: 'benefit', timeFilter: true, title: '社会效益', desc: '多站累计节能减排成效' },
      {
        type: 'cards', title: '社会效益 · 多站累计',
        items: [
          { name: '节约标准煤', state: '社会效益', kvs: [['累计值', `${BENEFIT.coalSaving.toLocaleString()} kg`], ['口径', '总放电量 × 0.284 kg/kWh']] },
          { name: '二氧化碳减排总量', state: '社会效益', kvs: [['累计值', `${BENEFIT.co2Reduction.toLocaleString()} kg`], ['口径', '总放电量 × 0.581 kg/kWh']] },
          { name: '等效种树量', state: '社会效益', kvs: [['累计值', `${BENEFIT.trees.toLocaleString()} 棵`], ['口径', 'CO₂减排量 ÷ 18.3']] },
        ],
      },
      ];
    },
  },

  // doc01 2.2：卡片栏 / 收益指标 / 电量指标 / 系统效率 / 告警指标
  '/app/display/multi': {
    filters: [
      { key: 'scope', type: 'select', label: '时间范围', options: ['近7日', '当月', '当年', '全部'].map((v) => ({ value: v, label: v })) },
      F_STATION,
    ],
    actions: [A_REFRESH, { key: 'custom', label: '自定义布局', primary: true }],
    blocks: [
      {
        type: 'kpi', title: '卡片栏 · 站点规模',
        items: [
          { label: '电站个数', value: String(MULTI_M.count), unit: '个' },
          { label: '总装机功率', value: '2.55', unit: 'MW' },
          { label: '总装机容量', value: '4.1', unit: 'MWh' },
        ],
      },
      {
        type: 'kpi', title: '卡片栏 · 电量与收益',
        items: [
          { label: '总充电量', value: '528', unit: 'MWh', foot: '不含今日' },
          { label: '总放电量', value: '496', unit: 'MWh', foot: '不含今日' },
          { label: '累计收益', value: '35.7', unit: '万元', foot: '不含今日' },
          { label: '昨日收益', value: '26,840', unit: '元' },
        ],
      },
      { type: 'section', key: 'income', timeFilter: true, title: '收益指标', desc: '各站点收益对比与单位电量收益排名' },
      {
        type: 'row', layout: 'grid-2',
        blocks: [
          {
            type: 'chart', title: '收益柱状图', extra: '各站点分时电量 × 分时电价 · 元', chartType: 'bar',
            data: STATION_INCOME, xKey: 'name', height: 250, barSize: 26,
            series: [{ key: 'income', name: '收益', color: '#f4c542' }],
          },
          {
            type: 'chart', title: '收益排名', extra: '按每 kWh 收益排名 · 元/kWh', chartType: 'bar',
            data: [...STATION_INCOME].sort((a, b) => b.perKwh - a.perKwh), xKey: 'name', height: 250, barSize: 26,
            series: [{ key: 'perKwh', name: '每 kWh 收益', color: '#00d4ff' }],
          },
        ],
      },
      { type: 'section', key: 'energy', timeFilter: true, title: '电量指标', desc: '近 7 日充放电量与累计电量' },
      {
        type: 'row', layout: 'grid-2',
        blocks: [
          {
            type: 'chart', title: '电量柱状图（充放电量）', extra: '单位：kWh', chartType: 'bar',
            timeRange: true, data: MULTI_ENERGY['近7日'], xKey: 'label', height: 250, barSize: 14,
            series: [
              { key: 'charge', name: '充电量', color: '#52e8b0' },
              { key: 'discharge', name: '放电量', color: '#4dd8ff' },
            ],
          },
          {
            type: 'stat', title: '电量指标',
            items: [
              { label: '累计充电量', value: '528', unit: 'MWh' },
              { label: '累计放电量', value: '496', unit: 'MWh' },
              { label: '当日充电量', value: '4,480', unit: 'kWh' },
              { label: '当日放电量', value: '4,080', unit: 'kWh' },
            ],
          },
        ],
      },
      { type: 'section', key: 'efficiency', timeFilter: true, title: '系统效率', desc: '系统效率趋势与站点效率排名' },
      {
        type: 'row', layout: 'grid-2',
        blocks: [
          {
            type: 'chart', title: '系统效率曲线（近7日）', extra: '单位：%',
            data: EFF_TREND, xKey: 'label', height: 250,
            series: [{ key: 'efficiency', name: '系统效率', color: '#52e8b0' }],
          },
          {
            type: 'chart', title: '效率排名', extra: '单位：%', chartType: 'bar',
            data: EFF_RANK, xKey: 'name', height: 250, barSize: 26,
            series: [{ key: 'efficiency', name: '系统效率', color: '#00d4ff' }],
          },
        ],
      },
      { type: 'section', key: 'alarm', timeFilter: true, title: '告警指标', desc: '告警次数、类型、等级与站点分布' },
      {
        type: 'row', layout: 'grid-2',
        blocks: [
          {
            type: 'chart', title: '告警次数统计（近7日）', extra: '单位：次', chartType: 'area',
            data: ALARM_TREND, xKey: 'label', height: 250,
            series: [{ key: 'count', name: '告警次数', color: '#ff5a7f', area: true }],
          },
          {
            type: 'chart', title: '设备告警分布', chartType: 'pie', data: ALARM_TYPE, height: 250,
            colors: ['#ff5a7f', '#f4c542', '#00d4ff', '#52e8b0'],
          },
        ],
      },
      {
        type: 'row', layout: 'grid-2',
        blocks: [
          {
            type: 'chart', title: '告警等级分布', chartType: 'pie', data: ALARM_LEVEL, height: 250,
            colors: ['#ff5a7f', '#f4c542', '#00d4ff', '#8b7cf6'],
          },
          {
            type: 'chart', title: '站点告警次数排名', extra: '单位：次', chartType: 'bar',
            data: ALARM_RANK, xKey: 'name', height: 250, barSize: 26,
            series: [{ key: 'count', name: '告警次数', color: '#ff5a7f' }],
          },
        ],
      },
    ],
  },

  // doc01 2.3：卡片栏 / 收益指标 / 电量指标 / 系统效率 / 告警指标 / 时间筛选 / 组件化
  '/app/display/single': {
    filters: [
      { key: 'station', type: 'select', label: '站点', width: 200, options: STATIONS.map((s) => ({ value: s.id, label: s.name })) },
      { key: 'scope', type: 'select', label: '时间范围', width: 140, options: ['近7日', '当月', '当年', '全部'].map((v) => ({ value: v, label: v })) },
    ],
    actions: [A_REFRESH, { key: 'custom', label: '自定义布局', primary: true }],
    blocks: [
      // doc01 2.3 p10：卡片栏需展示「电站图片」——作为卡片栏中的一张卡片（与站点信息同栏），
      // 缺陷 86：原先被渲染成整页一张巨图，改为受约束的卡片缩略图，不再占满页面
      {
        type: 'row', layout: 'grid-2',
        blocks: [
          {
            type: 'image', title: '电站图片', extra: '站点实景',
            alt: '电站图片', caption: '所选电站实景图，切换站点后由接口返回对应电站图片。',
          },
          {
            type: 'kpi', title: '卡片栏 · 站点信息',
            items: [
              { label: '装机功率', value: SINGLE_CARD.power.replace(' MW', ''), unit: 'MW' },
              { label: '装机容量', value: SINGLE_CARD.capacity.replace(' MWh', ''), unit: 'MWh' },
              { label: '并网点数量', value: String(SINGLE_CARD.gridPoints), unit: '个' },
              { label: '投运时间', value: SINGLE_CARD.onlineAt },
            ],
          },
        ],
      },
      {
        type: 'kpi', title: '卡片栏 · 电量与收益',
        items: [
          { label: '总充电量', value: SINGLE_CARD.chargeTotal.replace(' MWh', ''), unit: 'MWh', foot: '不含今日' },
          { label: '总放电量', value: SINGLE_CARD.dischargeTotal.replace(' MWh', ''), unit: 'MWh', foot: '不含今日' },
          { label: '今日充电量', value: SINGLE_CARD.chargeToday.replace(' kWh', ''), unit: 'kWh' },
          { label: '今日放电量', value: SINGLE_CARD.dischargeToday.replace(' kWh', ''), unit: 'kWh' },
          { label: '累计收益', value: SINGLE_CARD.incomeTotal.replace(' 万元', ''), unit: '万元', foot: '不含今日' },
          { label: '昨日收益', value: SINGLE_CARD.incomeYesterday.replace(' 元', ''), unit: '元' },
        ],
      },
      { type: 'section', key: 'incomeEnergy', timeFilter: true, title: '收益与电量指标' },
      {
        type: 'row', layout: 'grid-2',
        blocks: [
          {
            type: 'chart', title: '收益柱状图', extra: '分时电量 × 分时电价 · 元', chartType: 'bar',
            timeRange: true, data: SINGLE_INCOME['近7日'], xKey: 'label', height: 250, barSize: 20,
            series: [{ key: 'income', name: '收益', color: '#f4c542' }],
          },
          {
            type: 'chart', title: '电量柱状图（充放电量）', extra: '单位：kWh', chartType: 'bar',
            timeRange: true, data: SINGLE_ENERGY['近7日'], xKey: 'label', height: 250, barSize: 14,
            series: [
              { key: 'charge', name: '充电量', color: '#52e8b0' },
              { key: 'discharge', name: '放电量', color: '#4dd8ff' },
            ],
          },
        ],
      },
      { type: 'section', key: 'efficiency', timeFilter: true, title: '系统效率' },
      {
        type: 'row', layout: 'grid-2',
        blocks: [
          {
            type: 'chart', title: '系统效率曲线（近7日）', extra: '单位：%',
            data: SINGLE_EFF.trend, xKey: 'label', height: 250,
            series: [{ key: 'efficiency', name: '系统效率', color: '#52e8b0' }],
          },
          {
            type: 'stat', title: '系统效率指标',
            items: [
              { label: '近7日平均效率', value: String(SINGLE_EFF.value), unit: '%' },
              { label: '当月平均效率', value: (SINGLE_EFF.value - 0.2).toFixed(1), unit: '%' },
              { label: '当年平均效率', value: (SINGLE_EFF.value - 0.4).toFixed(1), unit: '%' },
            ],
          },
        ],
      },
      { type: 'section', key: 'alarm', timeFilter: true, title: '告警指标' },
      {
        type: 'row', layout: 'grid-2',
        blocks: [
          { type: 'chart', title: '设备告警分布', chartType: 'pie', data: SINGLE_ALARM.byType, height: 250, colors: ['#ff5a7f', '#f4c542', '#00d4ff', '#52e8b0'] },
          { type: 'chart', title: '告警等级分布', chartType: 'pie', data: SINGLE_ALARM.byLevel, height: 250, colors: ['#ff5a7f', '#f4c542', '#00d4ff', '#8b7cf6'] },
        ],
      },
      {
        type: 'chart', title: '告警次数统计（近7日）', extra: '单位：次', chartType: 'area',
        data: SINGLE_ALARM.trend, xKey: 'label', height: 260,
        series: [{ key: 'count', name: '告警次数', color: '#ff5a7f', area: true }],
      },
    ],
  },

  // ==================== 监控中心 ====================
  // 2.6 实时运行详情（演示：默认展开海宁工业园站）
  '/app/monitor/realtime': {
    filters: [...F_REGION, F_STATION_NAME],
    actions: [A_QUERY, A_RESET, A_REFRESH],
    autoRefresh: 5,
    blocks: [
      {
        type: 'table', title: '电站列表', extra: '5 秒刷新',
        page: false, scrollX: 1500,
        columns: [
          // 评审 56(用户 41)：「进入监控」按钮与站点名称同格绑定，直接进入该站单站监控
          { title: '站点名称', dataIndex: 'name', accent: true, width: 210, rowAction: true, actionKey: 'enterStation', actionLabel: '进入监控' },
          { title: '云站通信状态', dataIndex: 'comm', dot: true, dotMap: { 正常: 'online', 延迟: 'warn', 中断: 'offline' }, width: 120 },
          { title: 'PCS 工作状态', dataIndex: 'pcsState', width: 170, expandKey: 'pcsDetail', expandTitle: 'PCS 运行明细（哪些运行 / 哪些停机）' },
          { title: '总有功功率', dataIndex: 'power', width: 130 },
          { title: 'SOC（最高/最低）', dataIndex: 'soc', width: 160, suffix: '%' },
          { title: '今日充放电量', dataIndex: 'energy', width: 200 },
          { title: '空调温度（高/低）', dataIndex: 'hvac', width: 160 },
          { title: '今日告警次数', dataIndex: 'alarmCount', num: true, width: 120 },
          { title: '今日循环次数', dataIndex: 'cycle', num: true, width: 120 },
        ],
        rows: getRealtimeList(),
      },
      { type: 'info', title: '站点详情 · 基本信息', extra: '海宁工业园站', cols: 4, items: REALTIME_DETAIL.info },
      {
        type: 'row', layout: 'grid-2',
        blocks: [
          {
            type: 'chart', title: '充放电电量及系统效率（近 7 日）', extra: '左轴 kWh · 右轴 %', chartType: 'composed', data: REALTIME_DETAIL.energy, xKey: 'label', height: 240,
            series: [
              { key: 'charge', name: '充电量(kWh)', color: '#4dd8ff', type: 'bar' },
              { key: 'discharge', name: '放电量(kWh)', color: '#52e8b0', type: 'bar' },
              // 用户 42：效率与电量量纲不同，放右轴，否则 86% 的曲线会被 2 万 kWh 的坐标压成一条直线
              { key: 'efficiency', name: '系统效率(%)', color: '#f4c542', dashed: true, axis: 'right' },
            ],
          },
          {
            type: 'chart', title: '总有功功率（今日 / 昨日对比）', data: REALTIME_DETAIL.power, xKey: 'clock', height: 240, interval: 2, zeroLine: true,
            series: [
              { key: 'today', name: '今日(kW)', color: '#00d4ff', curve: 'stepAfter' },
              { key: 'yesterday', name: '昨日(kW)', color: '#8b7cf6', dashed: true, curve: 'stepAfter' },
            ],
          },
        ],
      },
      {
        type: 'table', title: '设备故障告警（当日）', extra: '按发生时间由晚到早 · 最多 20 条',
        columns: [
          { title: '发生时间', dataIndex: 'time', width: 120 },
          { title: '等级', dataIndex: 'level', tagMap: { 严重: 'red', 警告: 'orange', 提示: 'blue' }, width: 90 },
          { title: '告警内容', dataIndex: 'title', strong: true },
          { title: '设备', dataIndex: 'device', width: 180 },
          { title: '数值', dataIndex: 'value', width: 110 },
          { title: '状态', dataIndex: 'state', tagMap: { 待处理: 'orange', 已确认: 'blue', 已恢复: 'green' }, width: 100 },
        ],
        rows: REALTIME_DETAIL.alarms,
      },
    ],
  },

  // 2.9 集控管理
  '/app/monitor/central': {
    filters: [...F_REGION, F_STATION_NAME],
    actions: [{ key: 'batchTariff', label: '批量配置电价', primary: true }, A_QUERY, A_RESET],
    blocks: [
      {
        type: 'table', title: '站点集控列表', extra: '勾选站点后可批量操作', selectable: true,
        selectedKeys: ['hn', 'nx'],
        columns: [
          { title: '站点名称', dataIndex: 'name', accent: true },
          { title: '类型', dataIndex: 'typeLabel' },
          { title: '所在区域', dataIndex: 'region' },
          { title: '策略版本', dataIndex: 'strategy' },
          { title: '电价方案', dataIndex: 'tariff' },
          { title: '通信', dataIndex: 'comm', dot: true, dotMap: { 正常: 'online', 延迟: 'warn', 中断: 'offline' } },
          { title: '下发状态', dataIndex: 'dispatch', tagMap: { 已同步: 'green', 待下发: 'orange', 下发失败: 'red' } },
        ],
        rows: STATIONS.map((s) => ({
          key: s.id, name: s.name, typeLabel: s.typeLabel,
          region: `${STATION_REGIONS[s.id]?.province || ''}${STATION_REGIONS[s.id]?.city || ''}${STATION_REGIONS[s.id]?.district || ''}`,
          strategy: s.strategyOn ? '削峰填谷 v2' : '未启用',
          tariff: '谷/平/峰/尖',
          comm: s.comm === 'normal' ? '正常' : s.comm === 'delay' ? '延迟' : '中断',
          dispatch: s.comm === 'normal' ? '已同步' : '待下发',
        })),
      },
      {
        type: 'form', title: '批量配置电价', extra: '所选站点统一下发 · 支持修改电价与时间段（doc01 2.9 缺陷 69）', editable: true,
        fields: [
          { label: '谷电价', value: CENTRAL_TARIFF.valley.price, input: 'price', time: '00:00-06:00 / 23:00-24:00' },
          { label: '平电价', value: CENTRAL_TARIFF.flat.price, input: 'price', time: '06:00-08:00 / 11:00-13:00 / 22:00-23:00' },
          { label: '峰电价', value: CENTRAL_TARIFF.peak.price, input: 'price', time: '08:00-11:00 / 13:00-19:00' },
          { label: '尖电价', value: CENTRAL_TARIFF.sharp.price, input: 'price', time: '19:00-22:00' },
          { label: '生效方式', value: '立即生效' },
          { label: '权限校验', value: '需二次口令' },
        ],
      },
    ],
  },

  // ==================== 分析中心 ====================
  // 依据《能源管家》2.13 电池分析：电压分析 / 电流分析 / 温度分析 / 落后电池报表
  // 缺陷 91/92：一级 4 个 tab（各含二级子 tab）；筛选控件统一放在各图 / 表格卡片内、内容上方。
  // 文档 p51：页面顶部需提供「站点」选择，切换站点后加载该站点的电池分析数据
  '/app/analysis/battery': {
    filters: [
      {
        key: 'station', type: 'select', label: '站点', width: 200,
        defaultValue: STATIONS[0].id, noRowFilter: true,
        options: STATIONS.map((s) => ({ value: s.id, label: s.name })),
      },
    ],
    actions: [],
    blocks: [
      {
        type: 'tabs',
        items: [
          // ---------- 电压分析：电堆 / 电池簇 / 单体 ----------
          {
            key: 'voltage', label: '电压分析',
            blocks: [
              {
                type: 'tabs',
                items: [
                  {
                    key: 'stackV', label: '电堆电压预警',
                    blocks: [
                      {
                        type: 'kpi',
                        items: [
                          { label: '电堆电压极差', value: String(V_WARN.range), unit: 'mV', foot: '最大簇 − 最小簇', down: true },
                          { label: '标准差 σ', value: String(V_WARN.std), unit: 'mV', foot: '簇间电压离散度' },
                          { label: '簇端最高 / 最低', value: `${V_WARN.clusterMax} / ${V_WARN.clusterMin}`, unit: 'V' },
                          { label: '单体最高 / 最低', value: `${V_WARN.cellMax} / ${V_WARN.cellMin}`, unit: 'V' },
                        ],
                      },
                      {
                        type: 'chart', title: '电堆电压预警', extra: 'y=电压 / V · x=时间（秒级）· 图例：极差 / 标准差',
                        controls: [ctlStack, ctlTime, btnQuery, btnReset],
                        data: getStackVoltageWarnSeries(), xKey: 'time', height: 260,
                        series: [
                          { key: 'range', name: '极差', color: '#f4c542' },
                          { key: 'std', name: '标准差', color: '#00d4ff' },
                        ],
                      },
                    ],
                  },
                  {
                    key: 'rackV', label: '电池簇电压预警',
                    blocks: [
                      {
                        type: 'chart', title: '电池簇单体电压集群分析', extra: 'y=电压 / V · 图例：平均值 / 最大值 / 最小值',
                        controls: [ctlStack, ctlRack, ctlTime, btnQuery, btnReset],
                        data: getClusterVoltageSeries(), xKey: 'clock', height: 250,
                        series: [
                          { key: 'avg', name: '单体电压平均值', color: '#00d4ff' },
                          { key: 'max', name: '单体电压最大值', color: '#ff5a7f' },
                          { key: 'min', name: '单体电压最小值', color: '#52e8b0' },
                        ],
                      },
                      {
                        type: 'chart', title: '电池簇单体压差分布', extra: 'y=压差 / V · x=单体编号', chartType: 'bar',
                        controls: [ctlStack, ctlRack, ctlTime, btnQuery, btnReset],
                        data: getCellVoltageDiffDistribution(), xKey: 'cell', height: 240, barSize: 3, interval: 11,
                        series: [{ key: 'diff', name: '压差 / V', color: '#3a8dde' }],
                      },
                      {
                        type: 'chart', title: '电池簇单体压差趋势', extra: 'y=压差 / V · x=日期 · 同一单体不同日期',
                        controls: [ctlStack, ctlRack, ctlCell, ctlTime, btnQuery, btnReset],
                        data: getCellVoltageDiffTrend(), xKey: 'day', height: 240,
                        series: [
                          { key: 'c38', name: '38#', color: '#ff5a7f' },
                          { key: 'c61', name: '61#', color: '#f4c542' },
                          { key: 'c12', name: '12#', color: '#52e8b0' },
                        ],
                      },
                    ],
                  },
                  {
                    key: 'cellV', label: '单体电压分析',
                    blocks: [
                      {
                        type: 'chart', title: '单体电压分布', extra: 'y=电压 / mV · x=单体编号',
                        controls: [ctlStack, ctlRack, ctlTime, btnQuery, btnReset],
                        data: getCellVoltageDistribution(), xKey: 'cell', height: 240, barSize: 3, interval: 11,
                        yDomain: [3280, 3400],
                        series: [{ key: 'voltage', name: '单体电压 / mV', color: '#00d4ff' }],
                      },
                      {
                        type: 'table', title: '单体电压一致性明细', extra: '平均电池电压：3348 mV',
                        controls: [ctlConsistency, btnQuery, btnReset, { key: 'evaluateV', type: 'button', label: '评价电压标准差', primary: true, tip: '已按评价标准计算单体电压一致性等级' }],
                        page: 10, scrollX: 720,
                        columns: [
                          { title: '单体编号', dataIndex: 'cell', num: true },
                          { title: '单体电压 (mV)', dataIndex: 'voltage', num: true },
                          { title: '平均电池电压 (mV)', dataIndex: 'avg', num: true },
                          { title: '电池组', dataIndex: 'group', num: true },
                          { title: '一致性', dataIndex: 'consistency', dot: true, dotMap: { 优: 'online', 良: 'online', 中: 'warn', 差: 'fault' } },
                        ],
                        rows: getCellVoltageTable(),
                      },
                      {
                        type: 'chart', title: '单体电压曲线', extra: 'y=电压 / V · x=日期 · 同一单体',
                        controls: [ctlStack, ctlRack, ctlCell, ctlTime, btnQuery, btnReset],
                        data: getCellVoltageCurve(), xKey: 'time', height: 240,
                        series: [{ key: 'v', name: '单体1', color: '#00d4ff' }],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          // ---------- 电流分析：按文档 2.13.2 只有一个子项「电堆电流预警」 ----------
          {
            key: 'current', label: '电流分析',
            blocks: [
              {
                type: 'chart', title: '电堆电流预警', extra: 'y 左=极差 / A · y 右=标准差 / A · x=时间（秒级）· 图例：极差 / 标准差',
                controls: [ctlStack, ctlTime, btnQuery, btnReset],
                data: getStackCurrentWarnSeries(), xKey: 'time', height: 320, interval: 2,
                series: [
                  { key: 'range', name: '电池簇电流极差', color: '#f4c542' },
                  { key: 'std', name: '电池簇电流标准差', color: '#00d4ff', axis: 'right' },
                ],
              },
            ],
          },
          // ---------- 温度分析：电池簇 / 单体 ----------
          {
            key: 'temp', label: '温度分析',
            blocks: [
              {
                type: 'tabs',
                items: [
                  {
                    key: 'rackT', label: '电池簇温度预警',
                    blocks: [
                      {
                        type: 'chart', title: '电池簇温度分析', extra: 'y=温度 / ℃ · 图例：平均值 / 最大值 / 最小值',
                        controls: [ctlStack, ctlRack, ctlTime, btnQuery, btnReset],
                        data: getClusterTempAvgMaxMin(), xKey: 'clock', height: 250,
                        series: [
                          { key: 'avg', name: '单体温度平均值', color: '#00d4ff' },
                          { key: 'max', name: '单体温度最大值', color: '#ff5a7f' },
                          { key: 'min', name: '单体温度最小值', color: '#52e8b0' },
                        ],
                      },
                      {
                        type: 'chart', title: '电池簇温差预警', extra: 'y=温差 / ℃ · x=时间',
                        controls: [ctlStack, ctlRack, ctlTime, btnQuery, btnReset],
                        data: getClusterTempDiffSeries(), xKey: 'clock', height: 250,
                        series: [{ key: 'diff', name: '温差', color: '#00d4ff' }],
                      },
                      {
                        type: 'chart', title: '电池簇单体温升预警', extra: 'y=温升 / ℃ · x=时间 · 簇内多单体',
                        controls: [ctlStack, ctlRack, ctlCell, ctlTime, btnQuery, btnReset],
                        data: getCellTempRise(), xKey: 'clock', height: 250,
                        series: [
                          { key: 'c38', name: '38#', color: '#ff5a7f' },
                          { key: 'c61', name: '61#', color: '#f4c542' },
                          { key: 'c104', name: '104#', color: '#8b7cf6' },
                          { key: 'c1', name: '1#', color: '#52e8b0' },
                        ],
                      },
                    ],
                  },
                  {
                    key: 'cellT', label: '单体温度分析',
                    blocks: [
                      {
                        type: 'chart', title: '单体温度分布', extra: 'y=温度 / ℃ · x=单体编号',
                        controls: [ctlStack, ctlRack, ctlTime, btnQuery, btnReset],
                        data: getCellTempDistribution(), xKey: 'cell', height: 240, barSize: 3, interval: 11,
                        yDomain: [18, 34],
                        series: [{ key: 'temp', name: '单体温度 / ℃', color: '#00d4ff' }],
                      },
                      {
                        type: 'table', title: '单体温度一致性明细', extra: '平均电池温度：26 ℃',
                        controls: [ctlConsistency, btnQuery, btnReset, { key: 'evaluateT', type: 'button', label: '评价温度标准差', primary: true, tip: '已按评价标准计算单体温度一致性等级' }],
                        page: 10, scrollX: 700,
                        columns: [
                          { title: '单体编号', dataIndex: 'cell', num: true },
                          { title: '单体温度 (℃)', dataIndex: 'temp', num: true },
                          { title: '平均电池温度 (℃)', dataIndex: 'avg', num: true },
                          { title: '电池组', dataIndex: 'group', num: true },
                          { title: '一致性', dataIndex: 'consistency', dot: true, dotMap: { 优: 'online', 良: 'online', 中: 'warn', 差: 'fault' } },
                        ],
                        rows: getCellTempTable(),
                      },
                      {
                        type: 'chart', title: '单体温度曲线', extra: 'y=温度 / ℃ · x=日期 · 簇内多单体',
                        controls: [ctlStack, ctlRack, ctlCell, ctlTime, btnQuery, btnReset],
                        data: getCellTempCurve(), xKey: 'time', height: 240,
                        series: [{ key: 'v', name: '单体1', color: '#00d4ff' }],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          // ---------- 落后电池报表 ----------
          {
            key: 'lagging', label: '落后电池报表',
            blocks: [
              {
                type: 'table', title: '落后电池报表', extra: '默认：1#电堆 · 昨日 至 昨日',
                note: '自动遍历所有电池簇，找到该簇当天电压最低的 5 个电芯，按电压从低到高逐行排列。',
                scrollX: 1580,
                controls: [
                  ctlStack,
                  { key: 'time', type: 'daterange', label: '时间', defaultValue: BAT_YDAY, width: 230 },
                  btnQuery, btnReset,
                  { key: 'export', type: 'button', label: '导出', tip: '已导出「落后电池报表」为 Excel（文件名：落后电池报表 时间-时间）' },
                ],
                ...getLaggingReport(),
              },
            ],
          },
        ],
      },
    ],
  },

  // ==================== 运维中心 ====================
  // doc01 2.15 工单 / 2.16 计划 / 2.17 运维图谱已改为独立页面组件
  // （src/pages/om/WorkOrderPage.js · PlanPage.js · GraphPage.js），支持真实增删改查与详情弹层；此处不再配置。

  // doc01 2.18 运维账号：站点名称 / 运维人数 / 姓名，10 条分页
  '/app/om/account': {
    filters: [
      { key: 'station', type: 'search', label: '站点名称', placeholder: '模糊查询' },
      { key: 'name', type: 'search', label: '姓名', placeholder: '模糊查询' },
    ],
    actions: [A_REFRESH, A_EXPORT],
    blocks: [
      {
        type: 'kpi',
        items: [
          { label: '运维站点数', value: String(OM_ACCOUNT_ROWS.length), unit: '个' },
          { label: '运维人员总数', value: String(OM_ACCOUNT_ROWS.reduce((s, r) => s + r.count, 0)), unit: '人' },
          { label: '平均每站人数', value: (OM_ACCOUNT_ROWS.reduce((s, r) => s + r.count, 0) / OM_ACCOUNT_ROWS.length).toFixed(1), unit: '人' },
          { label: '未绑定账号站点', value: '0', unit: '个' },
        ],
      },
      {
        type: 'table', title: '站点运维账号', extra: `共 ${OM_ACCOUNT_ROWS.length} 个站点 · 分页 10 条`,
        page: 10,
        columns: [
          { title: '站点名称', dataIndex: 'station', strong: true, width: 220 },
          { title: '运维人数', dataIndex: 'count', num: true, width: 120 },
          { title: '姓名', dataIndex: 'names', width: 260 },
        ],
        rows: OM_ACCOUNT_ROWS,
      },
    ],
  },

  // ==================== 运营中心 ====================
  // doc01 2.20：核算单 / 核算规则 / 电价模板
  '/app/biz/settle': {
    filters: [
      F_STATION,
      { key: 'kw', type: 'search', placeholder: '核算规则 / 电价模板 名称' },
      F_RANGE,
      { key: 'readResult', type: 'select', label: '抄表结果', options: ['成功', '失败'].map((v) => ({ value: v, label: v })) },
      { key: 'calcResult', type: 'select', label: '收益计算结果', options: ['成功', '失败'].map((v) => ({ value: v, label: v })) },
      { key: 'confirm', type: 'select', label: '确认状态', options: ['已确认', '未确认'].map((v) => ({ value: v, label: v })) },
    ],
    actions: [A_EXPORT, { key: 'gen', label: '手动发起核算', primary: true }],
    blocks: [
      {
        type: 'tabs',
        items: [
          {
            key: 'stat', label: '核算统计',
            blocks: [
              {
                type: 'kpi',
                items: [
                  { label: '近 12 月应收合计', value: SETTLE_STAT.last12.toLocaleString(), unit: '元' },
                  { label: '累计已确认金额', value: SETTLE_STAT.confirmed.toLocaleString(), unit: '元', up: true },
                  { label: '待确认金额', value: SETTLE_STAT.unconfirmed.toLocaleString(), unit: '元', down: true },
                  { label: '确认率', value: String(SETTLE_STAT.confirmRate), unit: '%' },
                  { label: '本月核算单', value: String(SETTLE_STAT.monthCount), unit: '单' },
                ],
              },
              {
                type: 'chart', title: '近 12 月应收金额', extra: '单位：元', chartType: 'bar',
                data: SETTLE_MONTH_TREND, xKey: 'month', height: 240, barSize: 18,
                series: [{ key: 'amount', name: '应收金额', color: '#52e8b0' }],
              },
              {
                type: 'table', title: '收益统计（按站点）', extra: '累计应收 / 已确认 / 确认率',
                scrollX: 940,
                columns: [
                  { title: '站点名称', dataIndex: 'station', strong: true },
                  { title: '核算单数', dataIndex: 'records', num: true },
                  MONEY('已确认金额 (元)', 'confirmedAmount'),
                  MONEY('待确认金额 (元)', 'unconfirmedAmount'),
                  { title: '确认率', dataIndex: 'rate' },
                  { title: '最近核算月份', dataIndex: 'latest' },
                ],
                rows: SETTLE_BY_STATION,
              },
            ],
          },
          {
            key: 'bills', label: '核算单',
            blocks: [
              {
                type: 'kpi',
                items: [
                  { label: '本月核算总数', value: String(SETTLE_RECORDS.length), unit: '单' },
                  { label: '本月已确认', value: String(SETTLE_RECORDS.filter((r) => r.state === '已确认').length), unit: '单', up: true },
                  { label: '本月未确认', value: String(SETTLE_RECORDS.filter((r) => r.state === '未确认').length), unit: '单', down: true },
                  { label: '累计已确认金额', value: '116,560', unit: '元' },
                ],
              },
              {
                type: 'table', title: '核算记录', extra: '按发起核算时间降序', scrollX: 1500, page: 10,
                columns: [
                  { title: '核算编号', dataIndex: 'no', accent: true, width: 150 },
                  { title: '站点名称', dataIndex: 'station' },
                  { title: '核算规则', dataIndex: 'rule' },
                  { title: '收益模式', dataIndex: 'mode' },
                  { title: '抄表周期', dataIndex: 'period' },
                  { title: '发起方式', dataIndex: 'by' },
                  { title: '抄表结果', dataIndex: 'readResult', tagMap: T.result },
                  { title: '收益计算结果', dataIndex: 'calcResult', tagMap: T.result },
                  MONEY('应收金额 (元)', 'amount'),
                  { title: '发起时间', dataIndex: 'genAt', width: 150 },
                  { title: '确认状态', dataIndex: 'state', tagMap: T.confirmState },
                ],
                rows: SETTLE_RECORDS,
              },
              {
                type: 'chart', title: '近 12 月应收金额', extra: '单位：元', chartType: 'bar',
                data: SETTLE_MONTH_TREND, xKey: 'month', height: 240, barSize: 18,
                series: [{ key: 'amount', name: '应收金额', color: '#52e8b0' }],
              },
            ],
          },
          {
            key: 'rules', label: '核算规则',
            blocks: [
              {
                type: 'table', title: '核算规则', extra: '按规则编号降序', scrollX: 1150, page: 10,
                columns: [
                  { title: '规则编号', dataIndex: 'no', accent: true },
                  { title: '规则名称', dataIndex: 'name', strong: true },
                  { title: '电价模板', dataIndex: 'tariff' },
                  { title: '收益模式', dataIndex: 'mode' },
                  { title: '折扣 / 分成', dataIndex: 'discount' },
                  { title: '发起类型', dataIndex: 'by' },
                  { title: '最近更新', dataIndex: 'updated', width: 120 },
                  { title: '状态', dataIndex: 'state', tagMap: T.ruleState },
                ],
                rows: SETTLE_RULES,
              },
            ],
          },
          {
            key: 'tariff', label: '电价模板',
            blocks: [
              {
                type: 'table', title: '电价模板', extra: '按模板编号降序', scrollX: 900,
                columns: [
                  { title: '模板编号', dataIndex: 'no', accent: true },
                  { title: '模板名称', dataIndex: 'name', strong: true },
                  { title: '已配置月份', dataIndex: 'months' },
                  { title: '最近更新月份', dataIndex: 'latest' },
                  { title: '更新时间', dataIndex: 'updated' },
                ],
                rows: TARIFF_TEMPLATES,
              },
            ],
          },
        ],
      },
    ],
  },

  // doc01 2.21 运营分析（增值服务）：多站报告 / 单站报告
  '/app/biz/analysis': {
    filters: [
      { key: 'month', type: 'select', label: '月份', width: 130, options: ['2026-09', '2026-08', '2026-07'].map((v) => ({ value: v, label: v })) },
      { key: 'province', type: 'select', label: '省份', width: 140, options: ['浙江省', '全部省份'].map((v) => ({ value: v, label: v })) },
      { key: 'state', type: 'select', label: '站点状态', width: 130, options: ['投运中', '建设中', '停运'].map((v) => ({ value: v, label: v })) },
    ],
    actions: [A_NEW('发起多站报告'), { key: 'single', label: '单站报告' }, A_EXPORT],
    blocks: [
      {
        type: 'tabs',
        items: [
          {
            key: 'multi', label: '多站报告',
            blocks: [
              {
                type: 'table', title: '多站报告列表', extra: '省份为「-」代表此报告包含所有省份的电站',
                columns: [
                  { title: '月份', dataIndex: 'month', strong: true, width: 110 },
                  { title: '省份', dataIndex: 'province', width: 120 },
                  { title: '站点总数', dataIndex: 'sites', num: true, width: 100 },
                  { title: '总装机功率', dataIndex: 'power', width: 120 },
                  { title: '总装机容量', dataIndex: 'capacity', width: 130 },
                  { title: '状态', dataIndex: 'state', tagMap: T.reportState, width: 110 },
                  { title: '生成时间', dataIndex: 'createdAt', width: 160 },
                ],
                rows: OP_MULTI_REPORTS,
              },
              {
                type: 'info', title: `总计区域 · ${OP_M.company}`,
                items: [
                  ['报告标题', `${OP_M.company} · ${OP_M.month}`],
                  ['报告统计周期', OP_M.period],
                  ['站点总数', `${OP_M.sites} 个`],
                  ['总装机功率 / 总装机容量', `${OP_M.power} / ${OP_M.capacity}`],
                  ['导出', '支持导出 word 报告'],
                ],
              },
              {
                type: 'stat', title: '运营概况',
                items: [
                  { label: '本月收益', value: OP_M.income.toLocaleString(), unit: '元' },
                  { label: '理论收益达成率', value: OP_M.theoryRate, unit: '%' },
                  { label: '单位容量收益', value: OP_M.unitIncome, unit: '元/kWh' },
                  { label: '环比上月', value: `+${OP_M.momRate}`, unit: '%' },
                  { label: '月充电量 · 理论 / 实际', value: `${OP_M.chargeTheory.toLocaleString()} / ${OP_M.chargeActual.toLocaleString()}`, unit: 'kWh' },
                  { label: '月放电量 · 理论 / 实际', value: `${OP_M.dischargeTheory.toLocaleString()} / ${OP_M.dischargeActual.toLocaleString()}`, unit: 'kWh' },
                  { label: '综合效率 · 理论 / 实际', value: `${OP_M.effTheory}% / ${OP_M.effActual}%` },
                ],
              },
              {
                type: 'row', layout: 'grid-2',
                blocks: [
                  {
                    type: 'chart', title: '各站理论收益达成率', extra: '散点分布以柱状示意', chartType: 'bar',
                    data: OP_STATION_DETAIL, xKey: 'name', height: 260,
                    series: [{ key: 'rate', name: '达成率 (%)', color: '#00d4ff' }],
                  },
                  {
                    type: 'chart', title: '各站评价比例', extra: '优秀 / 良好 / 待提升', chartType: 'pie',
                    data: OP_GRADE_PIE, height: 260,
                  },
                ],
              },
              {
                type: 'chart', title: '月度充放电量与收益', chartType: 'composed',
                data: MONTH_TREND, xKey: 'month', height: 260,
                series: [
                  { key: 'charge', name: '充电量 (kWh)', color: '#3a8dde' },
                  { key: 'discharge', name: '放电量 (kWh)', color: '#52e8b0' },
                  { key: 'income', name: '收益 (元)', color: '#f4c542', dashed: true },
                ],
              },
              {
                type: 'table', title: '各站明细', extra: '达成率 ≥ 80% 优秀，< 60% 待提升，其余良好 · 点击表头可排序',
                scrollX: 1400,
                columns: [
                  { title: '站点名称', dataIndex: 'name', strong: true, width: 200 },
                  { title: '评价等级', dataIndex: 'grade', tagMap: T.opGrade, width: 100 },
                  { title: '运行天数', dataIndex: 'days', num: true, width: 100 },
                  { title: '本月收益 (元)', dataIndex: 'income', num: true, width: 130 },
                  { title: '理论收益达成率 (%)', dataIndex: 'rate', num: true, width: 160 },
                  { title: '单位容量日收益 (元/kWh·天)', dataIndex: 'unitDay', num: true, width: 200 },
                  { title: '环比上月 (%)', dataIndex: 'mom', num: true, width: 130 },
                  { title: '操作', dataIndex: 'op', width: 90, accent: true },
                ],
                rows: OP_STATION_DETAIL.map((s) => ({ ...s, op: '详情' })),
              },
              {
                type: 'tabs',
                items: OP_SINGLE_TABS.map((s) => ({ key: s.site, label: `${s.site} 单站报告`, blocks: opSingleBlocks(s) })),
              },
            ],
          },
          {
            key: 'single', label: '单站报告',
            blocks: [
              {
                type: 'table', title: '单站报告列表', extra: `共 ${OP_SINGLE_REPORTS.length} 条`,
                page: 10,
                scrollX: 1400,
                columns: [
                  { title: '站点名称', dataIndex: 'site', strong: true, width: 200 },
                  { title: '月份', dataIndex: 'month', width: 100 },
                  { title: '装机功率', dataIndex: 'power', width: 110 },
                  { title: '装机容量', dataIndex: 'capacity', width: 120 },
                  { title: '运行天数', dataIndex: 'days', num: true, width: 100 },
                  { title: '本月收益 (元)', dataIndex: 'income', num: true, width: 130 },
                  { title: '理论收益达成率 (%)', dataIndex: 'rate', num: true, width: 160 },
                  { title: '状态', dataIndex: 'state', tagMap: T.reportState, width: 100 },
                  { title: '生成时间', dataIndex: 'createdAt', width: 160 },
                ],
                rows: OP_SINGLE_REPORTS,
              },
              {
                type: 'tabs',
                items: OP_SINGLE_TABS.map((s) => ({ key: s.site, label: `${s.site} · ${s.month}`, blocks: opSingleBlocks(s) })),
              },
            ],
          },
        ],
      },
    ],
  },

  // ==================== 后台：管理中心 ====================
  // doc01 3.1：所属站点 / 授权站点 / 标签管理
  '/app/admin/station': {
    filters: [
      { key: 'kw', type: 'search', placeholder: '站点名称' },
      { key: 'visible', type: 'select', label: '状态', options: ['显示', '隐藏'].map((v) => ({ value: v, label: v })) },
    ],
    actions: [A_NEW('新增站点'), A_EXPORT],
    blocks: [
      {
        type: 'tabs',
        items: [
          {
            key: 'own', label: `所属站点 (${OWN_STATIONS.length})`,
            blocks: [
              {
                type: 'table', title: '所属站点', extra: '本公司所有的站点，可新增', scrollX: 1500, page: 10,
                columns: [
                  { title: '站点名称', dataIndex: 'name', accent: true },
                  { title: '类型', dataIndex: 'typeLabel' },
                  { title: '位置', dataIndex: 'location' },
                  { title: '装机功率', dataIndex: 'power' },
                  { title: '装机容量', dataIndex: 'capacity' },
                  { title: '投运时间', dataIndex: 'onlineAt', width: 120 },
                  { title: '站点标签', dataIndex: 'tags' },
                  { title: '通信', dataIndex: 'comm', dot: true, textMap: { normal: '正常', delay: '延迟', offline: '离线' }, dotMap: { normal: 'online', delay: 'warn', offline: 'offline' } },
                  { title: '前台显示', dataIndex: 'visible', tagMap: T.visibleState },
                ],
                rows: OWN_STATIONS,
              },
            ],
          },
          {
            key: 'auth', label: `授权站点 (${AUTHORIZED_STATIONS.length})`,
            blocks: [
              {
                type: 'table', title: '授权站点', extra: '电站所有权为其他公司，授权给本公司', scrollX: 1550, page: 10,
                columns: [
                  { title: '站点名称', dataIndex: 'name', accent: true },
                  { title: '类型', dataIndex: 'typeLabel' },
                  { title: '位置', dataIndex: 'location' },
                  { title: '装机功率', dataIndex: 'power' },
                  { title: '装机容量', dataIndex: 'capacity' },
                  { title: '投运时间', dataIndex: 'onlineAt', width: 120 },
                  { title: '授权来源公司', dataIndex: 'source' },
                  { title: '站点标签', dataIndex: 'tags' },
                  { title: '前台显示', dataIndex: 'visible', tagMap: T.visibleState },
                ],
                rows: AUTHORIZED_STATIONS,
              },
            ],
          },
          {
            key: 'tags', label: '标签管理',
            blocks: [
              {
                type: 'table', title: '站点标签', extra: '可新增 / 编辑 / 查看 / 删除',
                columns: [
                  { title: '标签名称', dataIndex: 'name', accent: true },
                  { title: '关联站点数', dataIndex: 'count', num: true },
                ],
                rows: STATION_TAGS,
              },
            ],
          },
        ],
      },
      {
        type: 'info', title: '站点配置项',
        items: [
          ['设备台账', 'PCS / 电池堆 / 电表 / 空调 / 液冷 / 光伏 / 充电桩'],
          ['拓扑配置', '主接线图节点与连线'],
          ['电价绑定', '分时电价方案'],
          ['采集点', '遥测 / 遥信点表'],
        ],
      },
    ],
  },

  // doc01 3.2：组织成员 —— 左侧部门树 + 查询区 + 账号列表 + 添加成员 / 账号操作
  '/app/admin/member': {
    filters: [
      { key: 'role', type: 'select', label: '角色', width: 160, options: H_ROLES.map((r) => ({ value: r.name, label: r.name })) },
      { key: 'state', type: 'select', label: '状态', width: 120, options: [{ value: '启用', label: '启用' }, { value: '禁用', label: '禁用' }] },
      { key: 'name', type: 'search', label: '姓名', placeholder: '输入姓名模糊搜索' },
    ],
    actions: [A_NEW('添加成员'), A_EXPORT],
    blocks: [
      {
        type: 'row', layout: 'grid-2', style: { gridTemplateColumns: '260px 1fr' },
        blocks: [
          { type: 'tree', title: '部门', checkable: false, treeData: H_DEPT_TREE },
          {
            type: 'table', title: '账号列表', extra: `共 ${H_MEMBERS.length} 个账号`,
            columns: [
              { title: '账号名', dataIndex: 'account', accent: true },
              { title: '姓名', dataIndex: 'name', strong: true },
              { title: '角色', dataIndex: 'role' },
              { title: '手机号', dataIndex: 'phone' },
              { title: '部门', dataIndex: 'dept' },
              { title: '状态', dataIndex: 'state', tagMap: { 启用: 'green', 禁用: 'default' } },
            ],
            rows: H_MEMBERS,
          },
        ],
      },
      {
        type: 'tabs',
        items: [
          {
            key: 'add', label: '添加成员',
            blocks: [
              {
                type: 'form', title: '成员账号基本信息（除部门外均为必填）', cols: 2,
                fields: [
                  { label: '账号名 *', value: '请输入登录账号' },
                  { label: '姓名 *', value: '请输入成员姓名' },
                  { label: '手机号 *', value: '请输入 11 位手机号' },
                  { label: '角色 *', value: '请选择功能角色' },
                  { label: '部门', value: '默认部门（可空）' },
                  { label: '初始密码', value: 'Aa123456' },
                ],
              },
              {
                type: 'info', title: '站点分配方式（单选，默认省市区）',
                items: [
                  ['省市区', '拥有选中地区的全部电站权限'],
                  ['站点标签', '拥有选中标签的全部电站权限'],
                  ['外部公司', '拥有选中外部合作关系公司授权给本公司的全部电站权限（仅限授权站点）'],
                  ['手动选择', '默认全部，拥有选中的所有电站权限'],
                ],
              },
            ],
          },
          {
            key: 'ops', label: '账号操作',
            blocks: [
              {
                type: 'list', title: '操作说明',
                items: [
                  { tag: '编辑', color: 'blue', title: '编辑成员', desc: '跳转【编辑用户】页面，校验输入信息格式正确后确定即保存' },
                  { tag: '重置', color: 'orange', title: '重置密码', desc: '密码重置为 Aa123456，确定后提示「重置成功！」' },
                  { tag: '禁用', color: 'red', title: '禁用 / 启用', desc: '账号禁用后无法登录，登录时提醒「账号被禁用，请联系管理员」' },
                  { tag: 'Token', color: 'green', title: '生成 token', desc: '增值收费服务；token 仅生成一次并拼接在账号名后，悬停可查看完整 token；开放 API 被收回则不展示并隐藏已生成 token' },
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // doc01 3.3：角色管理 —— 查询区 + 新增 + 角色列表 + 权限配置 + 操作说明
  '/app/admin/role': {
    filters: [
      { key: 'name', type: 'search', label: '角色名称', placeholder: '输入角色名称模糊查询' },
    ],
    actions: [A_NEW('新增')],
    blocks: [
      {
        type: 'row', layout: 'grid-2',
        blocks: [
          {
            type: 'table', title: '角色列表', extra: `共 ${H_ROLES.length} 个角色`,
            columns: [
              { title: '角色名称', dataIndex: 'name', accent: true },
              { title: '创建时间', dataIndex: 'created' },
              { title: '创建人', dataIndex: 'creator' },
              { title: '成员数', dataIndex: 'members' },
              { title: '类型', dataIndex: 'typeLabel', tagMap: { 内置: 'blue', 自定义: 'default' } },
            ],
            rows: H_ROLES.map((r) => ({ ...r, typeLabel: r.builtin ? '内置' : '自定义' })),
          },
          {
            type: 'tree', title: '权限配置', checkable: true,
            checkedKeys: ['p-display', 'p-display-multi', 'p-monitor', 'p-monitor-realtime', 'p-monitor-station', 'p-alarm', 'p-alarm-fault'],
            treeData: PERM_TREE,
          },
        ],
      },
      {
        type: 'list', title: '角色操作',
        items: [
          { tag: '新增', color: 'blue', title: '新增角色', desc: '弹窗输入新增角色名称' },
          { tag: '编辑', color: 'blue', title: '编辑', desc: '编辑修改角色名称' },
          { tag: '详情', color: 'default', title: '详情', desc: '查看该角色名称及配置的菜单权限' },
          { tag: '复制', color: 'cyan', title: '复制', desc: '弹窗填写角色名称，可复制该角色的菜单权限' },
          { tag: '配置', color: 'green', title: '权限配置', desc: '进入二级页面，分配该角色的菜单权限' },
          { tag: '删除', color: 'red', title: '删除', desc: '正在被成员账号使用的角色不可删除' },
        ],
      },
    ],
  },

  // doc01 3.4：外部公司 —— 查询区 + 关联外部公司 + 列表 + 权限分配 / 关联说明
  '/app/admin/company': {
    filters: [
      { key: 'name', type: 'search', label: '公司名称', placeholder: '输入公司名称模糊查询' },
    ],
    actions: [A_NEW('关联外部公司'), A_EXPORT],
    blocks: [
      {
        type: 'table', title: '外部公司', extra: `共 ${H_COMPANIES.length} 家`,
        columns: [
          { title: '公司名称', dataIndex: 'name', accent: true },
          { title: '社会信用代码', dataIndex: 'credit' },
          { title: '关联 / 创建时间', dataIndex: 'linkedAt', width: 140 },
          { title: '联系人', dataIndex: 'contact' },
          { title: '联系电话', dataIndex: 'phone' },
          { title: '授权电站', dataIndex: 'stations', num: true },
          { title: '电站权限', dataIndex: 'perms' },
          { title: '状态', dataIndex: 'state', tagMap: T.companyState },
        ],
        rows: H_COMPANIES,
      },
      {
        type: 'tabs',
        items: [
          {
            key: 'perm', label: '权限分配',
            blocks: [
              {
                type: 'table', title: '电站权限（默认查看）',
                columns: [
                  { title: '权限', dataIndex: 'name', accent: true, width: 140 },
                  { title: '说明', dataIndex: 'desc' },
                ],
                rows: H_COMPANY_PERMS,
              },
            ],
          },
          {
            key: 'link', label: '关联说明',
            blocks: [
              {
                type: 'list', title: '关联外部公司',
                items: [
                  { tag: '关联', color: 'blue', title: '输入社会信用代码', desc: '弹窗输入需要关联的合作公司社会信用代码' },
                  { tag: '已使用', color: 'green', title: '已使用轻舟系统', desc: '展示对应公司名称，点击确定建立双向关联关系' },
                  { tag: '未使用', color: 'orange', title: '未使用轻舟系统', desc: '有创建公司权限可点击「下一步」创建合作公司；无权限则无法关联' },
                  { tag: '解除', color: 'red', title: '解除关联', desc: '双方任意一方解除关联关系，则互相授权的电站权限均取消授权' },
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // doc01 3.5：日志管理 —— 默认近 7 日，按操作时间由近到远，显示时间 / 操作内容 / 账号名称
  '/app/admin/log': {
    filters: [
      { key: 'account', type: 'search', label: '账号名称', placeholder: '输入账号名称模糊查询' },
      F_RANGE,
    ],
    actions: [A_EXPORT, A_REFRESH],
    blocks: [
      {
        type: 'table', title: '日志', extra: '默认近 7 日，按操作时间由近到远排序',
        columns: [
          { title: '时间', dataIndex: 'time', width: 200 },
          { title: '操作内容', dataIndex: 'content', strong: true },
          { title: '账号名称', dataIndex: 'account', accent: true },
        ],
        rows: H_LOGS,
        page: 10,
      },
    ],
  },

// doc01 3.6：个性配置 —— 登录页 logo / 导航栏 logo / 网页 icon / 系统名称 + 校验弹窗
  '/app/admin/theme': {
    filters: [],
    actions: [{ key: 'save', label: '保存', primary: true }],
    blocks: [
      {
        type: 'cards', title: '品牌素材上传',
        items: H_THEME_ITEMS.map((t) => ({
          name: t.label,
          state: '已上传',
          dot: 'online',
          kvs: [['格式要求', t.format], ['尺寸要求', t.size], ['应用位置', t.usage], ['当前文件', t.value]],
        })),
      },
      {
        type: 'form', title: '系统名称', cols: 2,
        fields: [
          { label: '系统名称', value: '储能管家' },
          { label: '命名规则', value: '4 - 10 个中文字符' },
          { label: '应用位置', value: '数据大屏标题与网页系统名称' },
          { label: '当前状态', value: '已保存' },
          { label: '未操作模块', value: '默认沿用储能管家原有 LOGO 与系统名称' },
        ],
      },
    ],
  },
};

// 单站「系统管理」面板使用的子项配置（批次1）。
// 结构与 MODULE_PAGES 一致，供 BlockRenderer 复用。
export const STATION_SYSTEM_BLOCKS = {
  ems: {
    blocks: [
      {
        type: 'table', title: '时段策略',
        columns: [
          { title: '时段名称', dataIndex: 'name', accent: true },
          { title: '开始', dataIndex: 'start' },
          { title: '结束', dataIndex: 'end' },
          { title: '动作', dataIndex: 'action', tagMap: { 充电: 'blue', 放电: 'green' } },
          { title: '功率', dataIndex: 'power' },
          { title: '状态', dataIndex: 'enabled', tagMap: T.enabled },
        ],
        rows: EMS_PERIODS,
      },
    ],
  },
  tariff: {
    blocks: [
      {
        type: 'table', title: '分时电价', extra: '单位：¥/kWh',
        columns: [
          { title: '类型', dataIndex: 'kind', tagMap: T.tariffKind },
          { title: '时段', dataIndex: 'period' },
          { title: '电价', dataIndex: 'price' },
        ],
        rows: TARIFF_ROWS,
      },
    ],
  },
};